import { Progress, Select } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { markEventAsRead } from 'modules/socket/managers'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { ASharedModal, SharedModalTitle } from 'views/components/SharedModal'
import { TripOfferClosedReasons } from 'views/pages/LogisticsKR/types'

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`

interface FieldProps {
  closed_reason: TripOfferClosedReasons
}

interface Props {
  tripOfferId: string
  onSuccess: refetchFunc | (() => void)
  onClose: () => void
}

export const ClosedReasonModal: FC<Props> = ({ onSuccess, tripOfferId = '', onClose }) => {
  const { t } = useTranslation('logisticBids')
  const notify = useNotification()
  const { formProgress, formHandler } = useAFormHandler('tripOfferClosing', { id: tripOfferId })

  const formik = useFormik<FieldProps>({
    initialValues: {
      closed_reason: TripOfferClosedReasons.request_closed,
    },

    onSubmit: formHandler(
      async () => {
        await apiClient.post(endpoints.LogisticsKR.tripOfferClose(tripOfferId), formik.values)
        tripOfferId && (await markEventAsRead([{ model: 'tripoffer', object_id: tripOfferId }]))
      },
      {
        onSuccess: async () => {
          await onSuccess(true)
          onClose()
          notify(Progress.SUCCESS, { title: t('common:notifyError') })
        },
        onError: (error) => {
          formik.setErrors(error.errors)
        },
      },
    ),
  })

  const options = Object.keys(TripOfferClosedReasons).map((val) => ({
    value: val,
    label: t(`closed_reason.${val}`),
  }))

  return (
    <ASharedModal size="small" id="tripOfferClosed" onClose={onClose}>
      <SharedModalTitle>{t('closed_reason.title')}</SharedModalTitle>
      <ModalContentWrapper>
        {t('closed_reason.body')}
        <Select
          onChange={(val) => formik.setFieldValue('closed_reason', val)}
          options={options}
          value={formik.values.closed_reason}
          showSearch={false}
          fullWidth
          allowClear={false}
        />
      </ModalContentWrapper>
      <ADefaultModalFooter onClose={onClose} onConfirm={() => formik.submitForm()} progress={formProgress} />
    </ASharedModal>
  )
}
